import { useState, useEffect } from "react"
import { useWordPressAuth } from "@/components/wordpress-auth-provider"
import { Clock, Calendar, TrendingUp, Activity, RefreshCw } from "lucide-react"
import { fetchNotionDataFromWP, refreshNotionDataAndClearCache, fetchUserTasks, fetchUserTimesheets } from "@/lib/notion-client"
import type { Task, TimesheetEntry } from "@/lib/types"
import { calculateStats, formatDuration, formatTime, formatDate } from "@/lib/time-utils"
import { Button } from "@/components/ui/button"
import { loadDraftEntries } from "@/lib/draft-entries"

export default function DashboardPage() {
  const { user } = useWordPressAuth()
  const [tasks, setTasks] = useState<Task[]>([])
  const [timesheets, setTimesheets] = useState<TimesheetEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isRefreshing, setIsRefreshing] = useState(false)

  // Check feature permissions
  const wpConfig = typeof window !== 'undefined' ? (window as any).WP_COCKPIT : null
  const features = wpConfig?.features || {}
  const canAccessTracker = features.tracker === 'yes'

  useEffect(() => {
    if (user) {
      loadData()
    }
  }, [user])

  async function loadData() {
    setIsLoading(true)
    const isDev = (window as any).WP_COCKPIT?.devMode || false
    
    try {
      if (isDev) console.log('[DashboardPage] Fetching data from WordPress...')
      const resp = await fetchNotionDataFromWP()
      if (isDev) console.log('[DashboardPage] Response:', resp)
      
      if (resp && resp.status === 'ok' && resp.data) {
        if (resp.data.tasks) setTasks(resp.data.tasks as any)
        if (canAccessTracker && resp.data.timesheets) {
          // Merge server timesheets with local draft entries
          const serverTimesheets = resp.data.timesheets as any
          const draftEntries = loadDraftEntries()
          if (isDev) {
            console.log('[DashboardPage] Server timesheets:', serverTimesheets.length)
            console.log('[DashboardPage] Draft entries:', draftEntries.length)
          }
          // Only merge submitted entries from server with draft entries from localStorage
          const submittedFromServer = serverTimesheets.filter((t: any) => t.status === 'Submitted')
          const merged = [...submittedFromServer, ...draftEntries]
          if (isDev) console.log('[DashboardPage] Merged total:', merged.length)
          setTimesheets(merged)
        }
        return
      }
      
      // If no data from cache, fall back to dummy data
      if (isDev) console.warn('[DashboardPage] No cached data, falling back to dummy data')
      const userId = user?.id.toString() || "user-1"
      if (canAccessTracker) {
        const [tasksData, timesheetsData] = await Promise.all([fetchUserTasks(userId), fetchUserTimesheets(userId)])
        setTasks(tasksData)
        // Convert NotionTimesheet to TimesheetEntry format and merge with draft entries
        const convertedTimesheets: TimesheetEntry[] = timesheetsData.map(ts => ({
          id: ts.id,
          taskId: ts.taskId,
          startTime: ts.startTime.toISOString(),
          endTime: ts.endTime?.toISOString(),
          status: ts.status,
          userId: ts.userId,
          manualMinutes: ts.manualMinutes,
          isSubmitted: ts.isSubmitted,
        }))
        const draftEntries = loadDraftEntries()
        setTimesheets([...convertedTimesheets, ...draftEntries])
      } else {
        const tasksData = await fetchUserTasks(userId)
        setTasks(tasksData)
      }
    } catch (error) {
      console.error('[DashboardPage] Failed to load data:', error)
      // On error, try to load dummy data as fallback
      const userId = user?.id.toString() || "user-1"
      if (canAccessTracker) {
        const [tasksData, timesheetsData] = await Promise.all([fetchUserTasks(userId), fetchUserTimesheets(userId)])
        setTasks(tasksData)
        // Convert NotionTimesheet to TimesheetEntry format and merge with draft entries
        const convertedTimesheets: TimesheetEntry[] = timesheetsData.map(ts => ({
          id: ts.id,
          taskId: ts.taskId,
          startTime: ts.startTime.toISOString(),
          endTime: ts.endTime?.toISOString(),
          status: ts.status,
          userId: ts.userId,
          manualMinutes: ts.manualMinutes,
          isSubmitted: ts.isSubmitted,
        }))
        const draftEntries = loadDraftEntries()
        setTimesheets([...convertedTimesheets, ...draftEntries])
      } else {
        const tasksData = await fetchUserTasks(userId)
        setTasks(tasksData)
      }
    } finally {
      setIsLoading(false)
    }
  }

  async function handleRefresh() {
    setIsRefreshing(true)
    const isDev = (window as any).WP_COCKPIT?.devMode || false
    
    try {
      if (isDev) console.log('[DashboardPage] Forcing fresh fetch from n8n webhook...')
      const resp = await refreshNotionDataAndClearCache() // This calls fetchNotionDataFromWP({ force: true }) and clears task content cache
      if (isDev) console.log('[DashboardPage] Refresh response:', resp)
      
      if (resp && resp.status === 'ok' && resp.data) {
        if (resp.data.tasks) setTasks(resp.data.tasks as any)
        if (canAccessTracker && resp.data.timesheets) {
          // Merge server timesheets with local draft entries
          const serverTimesheets = resp.data.timesheets as any
          const draftEntries = loadDraftEntries()
          if (isDev) {
            console.log('[DashboardPage Refresh] Server timesheets:', serverTimesheets.length)
            console.log('[DashboardPage Refresh] Draft entries:', draftEntries.length)
          }
          // Only merge submitted entries from server with draft entries from localStorage
          const submittedFromServer = serverTimesheets.filter((t: any) => t.status === 'Submitted')
          const merged = [...submittedFromServer, ...draftEntries]
          if (isDev) console.log('[DashboardPage Refresh] Merged total:', merged.length)
          setTimesheets(merged)
        }
        if (isDev) console.log('[DashboardPage] Successfully loaded fresh data from webhook')
      } else {
        if (isDev) console.warn('[DashboardPage] Refresh returned no data', resp)
      }
    } catch (error) {
      console.error('[DashboardPage] Failed to refresh data:', error)
    } finally {
      setIsRefreshing(false)
    }
  }

  // Calculate time periods
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  
  const weekStart = new Date(today)
  weekStart.setDate(today.getDate() - today.getDay()) // Start of week (Sunday)
  
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)

  // Filter timesheets by date ranges
  const todayTimesheets = timesheets.filter(entry => {
    const entryDate = new Date(entry.startTime)
    entryDate.setHours(0, 0, 0, 0)
    return entryDate.getTime() === today.getTime()
  })

  const weekTimesheets = timesheets.filter(entry => {
    const entryDate = new Date(entry.startTime)
    return entryDate >= weekStart
  })

  const monthTimesheets = timesheets.filter(entry => {
    const entryDate = new Date(entry.startTime)
    return entryDate >= monthStart
  })

  // Calculate stats
  const todayStats = calculateStats(todayTimesheets)
  const weekStats = calculateStats(weekTimesheets)
  const monthStats = calculateStats(monthTimesheets)

  // Count active tasks (In Progress status)
  const activeTasks = tasks.filter(task => task.status === "In Progress").length

  // Get recent activity (last 5 entries)
  const recentEntries = [...timesheets]
    .sort((a, b) => new Date(b.startTime).getTime() - new Date(a.startTime).getTime())
    .slice(0, 5)

  const getTaskName = (taskId: string) => {
    return tasks.find(t => t.id === taskId)?.title || "Unknown Task"
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="mb-8 flex items-center justify-between animate-pulse">
            <div>
              <div className="h-9 bg-gray-200 dark:bg-gray-700 rounded w-64 mb-3"></div>
              <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48"></div>
            </div>
            <div className="h-10 w-24 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>

          {/* User Info Skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-8 animate-pulse">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-48 mb-4"></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i}>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-2"></div>
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-32"></div>
                </div>
              ))}
            </div>
          </div>

          {/* Stats Grid Skeleton */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                  </div>
                  <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>

          {/* Recent Activity Skeleton */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
            <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-40 mb-4"></div>
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="p-4 border dark:border-gray-700 rounded-lg">
                  <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">
              Welcome back, {user?.displayName || 'User'}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Your command center overview
            </p>
          </div>
          <Button 
            variant="outline" 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            className="gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? 'Refreshing...' : 'Refresh'}
          </Button>
        </div>

        {/* User Info Card */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Profile Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Display Name</p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">{user?.displayName || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Username</p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">{user?.username || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Email</p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">{user?.email || 'N/A'}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400">Role</p>
              <p className="text-base font-medium text-gray-900 dark:text-gray-100">
                {Array.isArray(user?.roles) ? user.roles.join(', ') : 'N/A'}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        {canAccessTracker && (isRefreshing ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6 animate-pulse">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 mb-3"></div>
                    <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
                  </div>
                  <div className="h-12 w-12 bg-gray-200 dark:bg-gray-700 rounded-full"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Today's Time</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatDuration(todayStats.totalMinutes)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Clock className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">This Week</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatDuration(weekStats.totalMinutes)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Calendar className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">This Month</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {formatDuration(monthStats.totalMinutes)}
                  </p>
                </div>
                <div className="h-12 w-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <TrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
              </div>
            </div>

            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">Active Tasks</p>
                  <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{activeTasks}</p>
                </div>
                <div className="h-12 w-12 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                  <Activity className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Recent Activity */}
        {canAccessTracker && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Recent Activity</h2>
            {isRefreshing ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="p-4 border dark:border-gray-700 rounded-lg animate-pulse">
                    <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                  </div>
                ))}
              </div>
            ) : recentEntries.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Activity className="h-12 w-12 mx-auto mb-3 text-gray-400 dark:text-gray-500" />
                <p>No recent activity</p>
                <p className="text-sm mt-1">Start tracking time to see your activity here</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentEntries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-4 border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 dark:text-gray-100">
                        {getTaskName(entry.taskId)}
                      </p>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {formatDate(entry.startTime)} at {formatTime(entry.startTime)}
                        {entry.endTime && ` - ${formatTime(entry.endTime)}`}
                      </p>
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        {formatDuration(
                          entry.endTime
                            ? Math.floor((new Date(entry.endTime).getTime() - new Date(entry.startTime).getTime()) / 1000 / 60)
                            : 0
                        )}
                      </p>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        entry.status === "Draft"
                          ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400"
                          : "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400"
                      }`}>
                        {entry.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Tasks Overview when tracker is disabled */}
        {!canAccessTracker && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border dark:border-gray-700 p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">Tasks Overview</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-blue-600 dark:text-blue-400 mb-2">
                  {tasks.filter(t => t.status === "In Progress").length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">In Progress</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400 mb-2">
                  {tasks.filter(t => t.status === "Not Started").length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Not Started</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600 dark:text-green-400 mb-2">
                  {tasks.filter(t => t.status === "Done").length}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400">Completed</div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
